/**
 * Utility functions for safely handling Apollo GraphQL responses and preventing
 * "null is not an object" errors when accessing response.data properties.
 */

export interface SafeApolloResult<T = any> {
  data: T;
  hasError: boolean;
  error?: any;
  isEmpty: boolean;
}

/**
 * Safely accesses Apollo query result data with proper error checking
 * @param result - Apollo query result
 * @param queryName - Name of the query property to access
 * @param defaultValue - Default value to return if data is null/undefined
 * @returns SafeApolloResult with data, error status, and emptiness flag
 */
export function safeGetQueryData<T>(
  result: any,
  queryName: string,
  defaultValue: T
): SafeApolloResult<T> {
  // Check if result exists and has no errors
  if (!result) {
    console.warn(`Apollo result is null/undefined for query: ${queryName}`);
    return {
      data: defaultValue,
      hasError: true,
      error: 'Result is null/undefined',
      isEmpty: true,
    };
  }

  // Check for Apollo errors
  if (result.error || result.errors) {
    console.error(
      `Apollo GraphQL error for query ${queryName}:`,
      result.error || result.errors
    );
    return {
      data: defaultValue,
      hasError: true,
      error: result.error || result.errors,
      isEmpty: true,
    };
  }

  // Check if data property exists
  if (!result.data) {
    console.warn(
      `Apollo result.data is null/undefined for query: ${queryName}`
    );
    return {
      data: defaultValue,
      hasError: true,
      error: 'Data property is null/undefined',
      isEmpty: true,
    };
  }

  // Check if the specific query data exists
  const queryData = result.data[queryName];
  if (queryData == null) {
    console.warn(`Query data for "${queryName}" is null/undefined`);
    return {
      data: defaultValue,
      hasError: false, // Not necessarily an error, might be empty result
      isEmpty: true,
    };
  }

  return {
    data: queryData,
    hasError: false,
    isEmpty: false,
  };
}
